import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

const FNW_COLLECTION_POLISH_ID = 'fnw-collection-card-runtime-polish';

function installFNWCollectionPolish() {
  if (document.getElementById(FNW_COLLECTION_POLISH_ID)) return;

  const style = document.createElement('style');
  style.id = FNW_COLLECTION_POLISH_ID;
  style.textContent = `
    @media screen and (min-width: 750px) {
      .fnw-collection-modern .product-grid.product-grid--grid[product-grid-view='default'] {
        --product-grid-columns-desktop: repeat(auto-fill, minmax(225px, 1fr)) !important;
        --product-grid-gap: 38px 24px !important;
      }
    }

    .fnw-collection-modern .product-grid__card a[ref='productTitleLink'] .text-block {
      max-width: 100%;
      width: 100% !important;
    }

    .fnw-collection-modern .product-grid__card a[ref='productTitleLink'] p[role='heading'] {
      color: rgb(var(--color-foreground-rgb) / 0.74) !important;
      display: -webkit-box !important;
      font-size: 0.82rem !important;
      font-weight: 520 !important;
      letter-spacing: 0 !important;
      line-height: 1.2 !important;
      max-height: 2.4em !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      text-wrap: pretty !important;
      -webkit-box-orient: vertical !important;
      -webkit-line-clamp: 2 !important;
      line-clamp: 2 !important;
    }

    .fnw-collection-modern .product-grid__card product-price,
    .fnw-collection-modern .product-grid__card .price,
    .fnw-collection-modern .product-grid__card .compare-at-price {
      color: rgb(var(--color-foreground-rgb) / 0.52) !important;
      font-size: 0.76rem !important;
      font-weight: 450 !important;
      line-height: 1.2 !important;
    }

    @media screen and (max-width: 749px) {
      .fnw-collection-modern .product-grid__card a[ref='productTitleLink'] p[role='heading'] {
        font-size: 0.78rem !important;
      }

      .fnw-collection-modern .product-grid__card product-price,
      .fnw-collection-modern .product-grid__card .price,
      .fnw-collection-modern .product-grid__card .compare-at-price {
        font-size: 0.72rem !important;
      }
    }
  `;
  document.head.append(style);
}

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();
    installFNWCollectionPolish();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
